import React, { useState, useEffect } from 'react';
import { useSairamUltimaSemanaData } from '@/hooks/useSairamUltimaSemanaData';
import DashboardTabs from '@/components/dashboard/DashboardTabs';
import ErrorDisplay from '@/components/dashboard/ErrorDisplay';
import { DashboardHeader, FilterSidebar } from '@/components/dashboard';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useIsMobile } from '@/hooks/use-mobile';
import { useSidebar } from '@/components/ui/sidebar';
import AppSidebar from '@/components/AppSidebar';
import { PropertyFilters } from '@/types/property';

const DashboardSairamUltimaSemana = () => {
  const [isFilterExpanded, setIsFilterExpanded] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [refreshTimedOut, setRefreshTimedOut] = useState(false);
  const [forcingLogout, setForcingLogout] = useState(false);
  const [filters, setFilters] = useState<PropertyFilters>({
    finalidade: [],
    tipo: [],
    bairro: [],
    cidade: [],
    origem: [],
    variacao: [],
  });
  const [bairroSearch, setBairroSearch] = useState('');
  const [tipoSearch, setTipoSearch] = useState('');

  const { user, session, refreshSession, signOut, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const { state } = useSidebar();

  const {
    loading,
    filteredProperties,
    uniqueValues,
    stats,
    dataLoaded,
    databaseError,
    applyFilters,
    fetchInitialData,
    calculateStats,
  } = useSairamUltimaSemanaData();

  const filteredStats = React.useMemo(() => {
    if (filteredProperties.length > 0) {
      return calculateStats(filteredProperties);
    }
    return stats;
  }, [filteredProperties, stats, calculateStats]);

  useEffect(() => {
    if (isMobile) {
      setIsFilterExpanded(false);
    }
  }, [isMobile]);

  const toggleFilterExpanded = () => {
    setIsFilterExpanded((prev) => !prev);
  };

  const handleClearFilters = () => {
    const resetFilters: PropertyFilters = {
      finalidade: [],
      tipo: [],
      bairro: [],
      cidade: [],
      origem: [],
      variacao: [],
    };
    setFilters(resetFilters);
    setBairroSearch('');
    setTipoSearch('');
    applyFilters(resetFilters);
  };

  const handleForceLogout = async () => {
    console.log('Force logout initiated in SairamUltimaSemana Dashboard');

    if (forcingLogout) {
      return;
    }

    setForcingLogout(true);
    setIsRefreshing(false);
    setRefreshTimedOut(true);

    try {
      await signOut();
      navigate('/login', { replace: true });
    } catch (error) {
      console.error('Error during force logout');
      navigate('/login', { replace: true });
    } finally {
      setForcingLogout(false);
    }
  };

  const handleDatabaseError = async () => {
    if (databaseError && (databaseError.includes('JWT') || databaseError.includes('auth'))) {
      console.log('Authentication error detected');

      if (isRefreshing) {
        return;
      }

      setIsRefreshing(true);
      setRefreshTimedOut(false);

      try {
        console.log('Attempting session refresh');
        const timeoutPromise = new Promise<boolean>((_, reject) => {
          const timeoutId = setTimeout(() => {
            console.log('Session refresh timed out');
            reject(new Error('Timeout'));
          }, 5000);

          return () => clearTimeout(timeoutId);
        });

        const success = await Promise.race([refreshSession(), timeoutPromise]);

        if (success) {
          console.log('Session refresh successful');
          toast.success('Sessão atualizada com sucesso');
          await fetchInitialData();
        } else {
          console.log('Session refresh failed');
          await handleForceLogout();
        }
      } catch (error) {
        console.error('Error refreshing session');
        await handleForceLogout();
      } finally {
        setIsRefreshing(false);
      }
    }
  };

  useEffect(() => {
    if (databaseError) {
      console.log('Database error detected:', databaseError?.substring(0, 50));
      handleDatabaseError();
    }
  }, [databaseError]);

  useEffect(() => {
    if (!user && !authLoading) {
      console.log('User not authenticated, redirecting to login');
      navigate('/login', { replace: true });
    }
  }, [user, authLoading, navigate]);

  useEffect(() => {
    if (user && !dataLoaded && !loading && !databaseError) {
      console.log('SairamUltimaSemana Dashboard: Fetching initial data');
      fetchInitialData();

      const cacheInfo = localStorage.getItem('sairamUltimaSemanaDataTimestamp');
      if (cacheInfo) {
        const timestamp = parseInt(cacheInfo, 10);
        const age = Math.floor((Date.now() - timestamp) / 60000);
        if (age < 60) {
          toast.info(`Usando dados atualizados há ${age} minutos`);
        }
      }
    }
  }, [fetchInitialData, user, dataLoaded, loading, databaseError]);

  useEffect(() => {
    setFilters((prev) => ({
      ...prev,
      valorMax: Math.ceil(stats.maxPrice / 100000) * 100000 || Number.MAX_SAFE_INTEGER,
    }));
  }, [stats.maxPrice]);

  if (authLoading) {
    return (
      <div className="flex items-center justify-center min-h-[calc(100vh-4rem)]">
        <Loader2 className="h-12 w-12 text-blue-dark animate-spin" />
      </div>
    );
  }

  if (isRefreshing && !refreshTimedOut) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[calc(100vh-4rem)]">
        <Loader2 className="h-12 w-12 text-blue-dark animate-spin mb-4" />
        <p className="text-gray-600">Atualizando sua sessão...</p>
        <p className="text-sm text-gray-500 mt-2">
          Se isso demorar mais de 5 segundos, você será redirecionado.
        </p>
        <Button
          variant="outline"
          className="mt-6"
          onClick={handleForceLogout}
          disabled={forcingLogout}
        >
          {forcingLogout ? 'Saindo...' : 'Sair agora'}
        </Button>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen w-full bg-white">
      <AppSidebar activePage="dashboard-sairam-ultima-semana" />

      <div
        className={`flex-1 overflow-auto ${state === 'collapsed' ? 'ml-10' : ''} ${
          isMobile ? 'mt-14' : ''
        }`}
      >
        <div className="container mx-auto py-3 md:py-6 px-2 md:px-6">
          <div className="flex flex-col space-y-4 md:space-y-6">
            <DashboardHeader 
              stats={filteredStats} 
              isMobile={isMobile} 
              dashboardType="sairam-ultima-semana" 
              loading={loading && !dataLoaded}
            />

            {databaseError && (
              <ErrorDisplay
                error={databaseError}
                onRetry={async () => {
                  console.log('Retrying data fetch');
                  await refreshSession();
                  fetchInitialData();
                }}
              />
            )}

            <div className="flex flex-col md:flex-row gap-4 md:gap-6">
              {isFilterExpanded && (
                <div className={isMobile ? 'w-full' : 'md:w-1/4'}>
                  <FilterSidebar
                    loading={loading}
                    dataLoaded={dataLoaded}
                    uniqueValues={uniqueValues}
                    stats={stats}
                    filteredStats={filteredStats}
                    applyFilters={applyFilters}
                    isExpanded={isFilterExpanded}
                    onToggleExpand={toggleFilterExpanded}
                    isMobile={isMobile}
                    filters={filters}
                    setFilters={setFilters}
                    bairroSearch={bairroSearch}
                    setBairroSearch={setBairroSearch}
                    tipoSearch={tipoSearch}
                    setTipoSearch={setTipoSearch}
                    clearFilters={handleClearFilters}
                  />
                </div>
              )}

              <div className="flex-1">
                <DashboardTabs
                  loading={loading}
                  dataLoaded={dataLoaded}
                  databaseError={databaseError}
                  filteredProperties={filteredProperties}
                  isFilterExpanded={isFilterExpanded}
                  onToggleFilterExpand={toggleFilterExpanded}
                  isMobile={isMobile}
                  dashboardType="sairam-ultima-semana"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardSairamUltimaSemana;
